/**
 * The shop's business day (2026-10-03). A day is a Tbilisi calendar day that
 * starts at the shop's own hour instead of midnight: Settings → „სამუშაო დღე
 * იწყება", the settings key `dayStartHour`, 0 (midnight — every shop until
 * now, and still the default) to 23.
 *
 * Why: SAMGORI2026 is a night shop — it opens ~18:00 and closes ~08:00. Cut
 * at midnight, every night fell into two calendar days and no day in
 * accounting matched the shift, the Z-report or the terminal's Z. With the day
 * starting at 12:00 the whole night is one day, named by its START date (the
 * owner's choice): 02.10 18:00 → 03.10 07:00 is „02.10".
 *
 * Every place that groups by day reads these, so the accounting screens, the
 * morning report and Telegram agree. Georgia is UTC+4 all year (no DST), so
 * the arithmetic is fixed-offset and the same in the browser and the Worker.
 */
const TBILISI_MS = 4 * 3_600_000
const HOUR_MS = 3_600_000
const DAY_MS = 86_400_000

/** A stored setting → a whole hour 0…23; anything else is midnight. */
export function parseDayStartHour(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN
  return Number.isInteger(n) && n >= 0 && n <= 23 ? n : 0
}

/** The business day (YYYY-MM-DD) an instant belongs to. */
export function businessDay(at: string | number | Date, startHour = 0): string {
  return new Date(new Date(at).getTime() + TBILISI_MS - startHour * HOUR_MS).toISOString().slice(0, 10)
}

/** Which whole business day an instant (ms) falls in, as a day number —
 *  every instant with the same number has the same `businessDay`. Lets a
 *  caller name each DAY once instead of formatting every row (1.26). */
export function businessDayNumber(ms: number, startHour = 0): number {
  return Math.floor((ms + TBILISI_MS - startHour * HOUR_MS) / DAY_MS)
}

/** YYYY-MM-DD moved by whole days. */
export function addDays(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10)
}

/** `[from, to)` of a business day as UTC ISO strings, for queries. */
export function businessDayRangeUtc(day: string, startHour = 0): { from: string; to: string } {
  const from = Date.parse(`${day}T00:00:00Z`) - TBILISI_MS + startHour * HOUR_MS
  return { from: new Date(from).toISOString(), to: new Date(from + DAY_MS).toISOString() }
}

/** The Tbilisi wall-clock hour of an instant. */
export function tbilisiHour(at: string | number | Date): number {
  return new Date(new Date(at).getTime() + TBILISI_MS).getUTCHours()
}

/**
 * The day a morning report covers, and the one before it for the comparison.
 *
 * Midnight shops: yesterday, as always. A shop whose day has not turned yet
 * when the report goes out (day starts 12:00, report at 08:00) is reported on
 * the day still running — for a night shop that is the night that just ended,
 * and it is closed until it turns. Asked again after the turn, the same night
 * is still the answer, because it is then „yesterday".
 */
export function reportDays(now: Date, startHour = 0): { day: string; prevDay: string } {
  const current = businessDay(now, startHour)
  const day = startHour > 0 && tbilisiHour(now) < startHour ? current : addDays(current, -1)
  return { day, prevDay: addDays(day, -1) }
}
